/* =====================================================================
   MV CART DRAWER JS  —  assets/mv-cart-drawer.js
   - Header cart / account icons open the drawer (tabs: Cart / Account)
   - Renders the cart from /cart.js, quantity +/-, remove, subtotal, checkout
   - Opens automatically after ANY add-to-cart (theme, quick add, valor-product…)
   - No dependency on Shopify.formatMoney (uses Intl)
   ===================================================================== */
(function () {
  'use strict';

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function MVCartDrawer(root) {
    this.root = root;
    this.panel = root.querySelector('.mv-drawer__panel');
    this.itemsNode = root.querySelector('[data-mv-cart-items]');
    this.noticeNode = root.querySelector('[data-mv-cart-notice]');
    this.footer = root.querySelector('[data-mv-cart-footer]');
    this.subtotal = root.querySelector('[data-mv-cart-subtotal]');
    this.emptyTemplate = root.querySelector('[data-mv-empty-cart-template]');
    this.views = root.querySelectorAll('[data-mv-view]');
    this.tabs = root.querySelectorAll('[data-mv-view-switch]');
    this.currency = root.getAttribute('data-currency') || 'USD';
    this.errorText = root.getAttribute('data-error') || 'We couldn\u2019t update your cart. Please try again.';
    this.cart = null;
    this.lastFocus = null;
    this.busy = false;
    this.bind();
    this.bindSignup();
    this.loadCart(true);
  }

  MVCartDrawer.prototype.bind = function () {
    var self = this;

    document.addEventListener('click', function (e) { self.handleClick(e); });

    document.addEventListener('keydown', function (e) {
      if (!self.isOpen()) return;
      if (e.key === 'Escape') { self.close(); return; }
      if (e.key === 'Tab') self.trapFocus(e);
    });

    /* Cart quantity typed by hand */
    this.root.addEventListener('change', function (e) {
      var input = e.target.closest('[data-mv-qty-input]');
      if (!input) return;
      var q = parseInt(input.value, 10);
      if (isNaN(q) || q < 0) q = 0;
      self.changeQuantity(input.getAttribute('data-key'), q);
    });

    /* Custom events other scripts / sections can fire */
    document.addEventListener('product:added', function () { self.open('cart'); });
    document.addEventListener('mov:cart-added', function () { self.open('cart'); });
    document.addEventListener('cart:refresh', function () { self.loadCart(); });

    /* Catch every add-to-cart made with fetch (theme forms, quick add, custom sections) */
    if (!window.__mvFetchPatched && typeof window.fetch === 'function') {
      window.__mvFetchPatched = true;
      var nativeFetch = window.fetch;
      window.fetch = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var p = nativeFetch.apply(this, arguments);
        if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
          p.then(function (r) {
            if (r && r.ok) document.dispatchEvent(new CustomEvent('product:added'));
          }).catch(function () {});
        }
        return p;
      };
    }

    /* Back/forward cache: refresh the cart when the page is restored */
    window.addEventListener('pageshow', function (e) { if (e.persisted) self.loadCart(true); });
  };

  MVCartDrawer.prototype.handleClick = function (event) {
    var t = event.target;

    var opener = t.closest('[data-mv-open]');
    if (opener) {
      event.preventDefault();
      this.open(opener.getAttribute('data-mv-open') === 'account' ? 'account' : 'cart');
      return;
    }

    if (t.closest('[data-mv-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    var tab = t.closest('[data-mv-view-switch]');
    if (tab && this.root.contains(tab)) {
      this.setView(tab.getAttribute('data-mv-view-switch'));
      return;
    }

    var qty = t.closest('[data-mv-qty]');
    if (qty && this.root.contains(qty)) {
      var key = qty.getAttribute('data-key');
      var delta = parseInt(qty.getAttribute('data-mv-qty'), 10);
      var item = this.findItem(key);
      if (item) this.changeQuantity(key, Math.max(0, item.quantity + delta));
      return;
    }

    var remove = t.closest('[data-mv-remove]');
    if (remove && this.root.contains(remove)) {
      this.changeQuantity(remove.getAttribute('data-key'), 0);
    }
  };

  /* ---------- open / close / tabs ---------- */
  MVCartDrawer.prototype.isOpen = function () { return this.root.classList.contains('is-open'); };

  MVCartDrawer.prototype.setView = function (name) {
    this.views.forEach(function (v) { v.hidden = v.getAttribute('data-mv-view') !== name; });
    this.tabs.forEach(function (b) {
      var on = b.getAttribute('data-mv-view-switch') === name;
      b.classList.toggle('is-active', on);
      if (on) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
    });
  };

  MVCartDrawer.prototype.open = function (view) {
    this.lastFocus = document.activeElement;
    this.setView(view || 'cart');
    this.root.classList.add('is-open');
    this.root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mv-drawer-lock');
    if (this.panel) this.panel.focus({ preventScroll: true });
    this.loadCart();
  };

  MVCartDrawer.prototype.close = function () {
    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mv-drawer-lock');
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus({ preventScroll: true });
  };

  MVCartDrawer.prototype.trapFocus = function (e) {
    var nodes = this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
    var list = Array.prototype.filter.call(nodes, function (n) { return n.offsetParent !== null; });
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  /* ---------- cart data ---------- */
  MVCartDrawer.prototype.findItem = function (key) {
    if (!this.cart) return null;
    for (var i = 0; i < this.cart.items.length; i++) if (this.cart.items[i].key === key) return this.cart.items[i];
    return null;
  };

  MVCartDrawer.prototype.loadCart = function (quiet) {
    var self = this;
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { self.render(cart); })
      .catch(function (err) { if (!quiet) self.notice(self.errorText); console.error(err); });
  };

  MVCartDrawer.prototype.changeQuantity = function (key, quantity) {
    var self = this;
    if (!key || this.busy) return;
    this.busy = true;
    this.notice('');
    this.itemsNode.classList.add('is-loading');
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Number(quantity) })
    })
      .then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok) throw new Error(j.description || j.message || self.errorText);
          return j;
        });
      })
      .then(function (cart) { self.render(cart); })
      .catch(function (err) { self.notice(err.message || self.errorText); self.loadCart(true); })
      .then(function () { self.busy = false; self.itemsNode.classList.remove('is-loading'); });
  };

  MVCartDrawer.prototype.notice = function (text) {
    if (this.noticeNode) this.noticeNode.textContent = text || '';
  };

  MVCartDrawer.prototype.money = function (cents, currency) {
    var code = currency || this.currency;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: code }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  MVCartDrawer.prototype.updateCount = function (count) {
    document.querySelectorAll('[data-mv-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = !count;
    });
  };

  MVCartDrawer.prototype.imageUrl = function (src) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=180';
  };

  MVCartDrawer.prototype.render = function (cart) {
    var self = this;
    this.cart = cart;
    this.currency = cart.currency || this.currency;
    this.updateCount(cart.item_count);

    if (!cart.items.length) {
      this.itemsNode.innerHTML = this.emptyTemplate ? this.emptyTemplate.innerHTML : '';
      this.footer.hidden = true;
      return;
    }

    this.footer.hidden = false;
    this.itemsNode.innerHTML = cart.items.map(function (item) {
      var img = self.imageUrl(item.image);
      var variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      var compare = item.original_line_price > item.final_line_price
        ? '<span class="mv-cart-item__compare">' + esc(self.money(item.original_line_price)) + '</span>' : '';
      return '' +
        '<div class="mv-cart-item">' +
          '<a class="mv-cart-item__media" href="' + esc(item.url) + '">' +
            (img ? '<img class="mv-cart-item__image" src="' + esc(img) + '" alt="' + esc(item.title) + '">' : '') +
          '</a>' +
          '<div>' +
            '<a class="mv-cart-item__title" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>' +
            (variant ? '<p class="mv-cart-item__variant">' + esc(variant) + '</p>' : '<p class="mv-cart-item__variant"></p>') +
            '<div class="mv-cart-item__quantity">' +
              '<button type="button" class="mv-cart-item__quantity-button" data-mv-qty="-1" data-key="' + esc(item.key) + '" aria-label="Decrease quantity">\u2212</button>' +
              '<input class="mv-cart-item__quantity-value" type="text" inputmode="numeric" value="' + item.quantity + '" data-mv-qty-input data-key="' + esc(item.key) + '" aria-label="Quantity">' +
              '<button type="button" class="mv-cart-item__quantity-button" data-mv-qty="1" data-key="' + esc(item.key) + '" aria-label="Increase quantity">+</button>' +
            '</div>' +
          '</div>' +
          '<div class="mv-cart-item__aside">' +
            '<span class="mv-cart-item__price">' + compare + esc(self.money(item.final_line_price)) + '</span>' +
            '<button type="button" class="mv-cart-item__remove" data-mv-remove data-key="' + esc(item.key) + '">Remove</button>' +
          '</div>' +
        '</div>';
    }).join('');

    this.subtotal.textContent = this.money(cart.total_price);
  };

  /* ---------- newsletter signup (AJAX, falls back to a normal submit) ---------- */
  MVCartDrawer.prototype.bindSignup = function () {
    var form = this.root.querySelector('#mv-signup-form');
    if (!form) return;
    var msg = this.root.querySelector('[data-mv-signup-message]');
    var ok = this.root.getAttribute('data-signup-ok') || '';
    var fail = this.root.getAttribute('data-signup-fail') || '';
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('button[type="submit"]');
      if (btn) btn.disabled = true;
      msg.textContent = '';
      fetch(form.action, { method: 'POST', body: new FormData(form), headers: { 'Accept': 'text/html' } })
        .then(function (r) {
          if (r.url.indexOf('/challenge') > -1) { form.submit(); return; }   /* captcha needed → normal submit */
          if (r.url.indexOf('customer_posted=true') > -1) { msg.textContent = ok; form.reset(); }
          else { msg.textContent = fail; }
        })
        .catch(function () { msg.textContent = fail; })
        .then(function () { if (btn) btn.disabled = false; });
    });
  };

  /* ---------- init ---------- */
  function init() {
    document.querySelectorAll('[data-mv-drawer]').forEach(function (el) {
      if (el.__mvDrawer) return;
      el.__mvDrawer = new MVCartDrawer(el);
      window.MVCartDrawerInstance = el.__mvDrawer;
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
